import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AccountHealth } from '../types';

interface HealthScoreCardProps {
  health: AccountHealth;
  orgId?: string;
  to?: string;
}

export default function HealthScoreCard({ health, orgId, to }: HealthScoreCardProps) {
  const { score, trend } = health;
  const navigate = useNavigate();
  const destination = to ?? (orgId ? `/intelligence/customers/${orgId}` : undefined);
  const isInteractive = Boolean(destination);
  const hasScore = typeof score === 'number';

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getTrendIcon = (trend: AccountHealth['trend']) => {
    switch (trend) {
      case 'up':
        return '↑';
      case 'down':
        return '↓';
      default:
        return '→';
    }
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!destination) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(destination);
    }
  };

  return (
    <Card
      className={cn(isInteractive && 'cursor-pointer transition-shadow hover:shadow-md')}
      role={isInteractive ? 'link' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? 'Open customer health page' : undefined}
      onClick={destination ? () => navigate(destination) : undefined}
      onKeyDown={isInteractive ? handleCardKeyDown : undefined}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
          Health Score
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm"
                  aria-label="Info about Health Score"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Info className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-muted-foreground cursor-help" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs">
                Composite score (0-100) based on sensor uptime, alert performance, device health, data quality, and engagement. Trend compares to the prior month when available.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-16 w-16 items-center justify-center rounded-full',
              hasScore ? getScoreBg(score) : 'bg-gray-100'
            )}
          >
            <span className={cn('text-2xl font-bold', hasScore ? getScoreColor(score) : 'text-gray-400')}>
              {hasScore ? score.toFixed(0) : '--'}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-1">
              <span
                className={cn(
                  'text-sm',
                  trend === 'up'
                    ? 'text-green-600'
                    : trend === 'down'
                      ? 'text-red-600'
                      : 'text-gray-500'
                )}
              >
                {getTrendIcon(trend)}
              </span>
              <span className="text-sm text-muted-foreground capitalize">
                {trend}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">vs last month</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
